import { formatOwnerNames, formatDateLong } from '@strenforcement/domain/formatting';
import { getMostRecent, getActivityDate } from '@strenforcement/domain/correspondence';

import {
  normalizeAddressLines as normalizeAddressLinesUtil,
  normalizeAddressCasing as normalizeAddressCasingUtil
} from '../addressFormatting.js';
import { formatDisplayName } from '../nameFormatting.js';

function toDateValue(value) {
  if (!value) return null;
  if (typeof value.toDate === 'function') {
    return value.toDate();
  }
  return value instanceof Date ? value : new Date(value);
}

export class LetterContext {
  constructor({
    property = null,
    license = null,
    listingUrl = null,
    evidence = null,
    listingId = null
  } = {}) {
    this._property = property || null;
    this._license = license || null;
    this._correspondences = property?.correspondences || [];
    this._listingUrl = listingUrl;
    this._evidence = evidence || null;
    this._listingId = listingId || null;
  }

  get property() {
    return this._property || this._license?.property || null;
  }

  get license() {
    return this._license;
  }

  get city() {
    return this.property?.city || this.license?.city || null;
  }

  get cityId() {
    return this.city?.id || null;
  }

  get licenseNumber() {
    return this.license?.number ?? null;
  }

  get dateFormatted() {
    return formatDateLong(new Date());
  }

  get correspondences() {
    return this._correspondences;
  }

  get dateOfLastLetter() {
    const mostRecent = getMostRecent(this.correspondences);
    if (!mostRecent) {
      return null;
    }
    return toDateValue(getActivityDate(mostRecent));
  }

  get dateOfLastLetterFormatted() {
    const date = this.dateOfLastLetter;
    return date ? formatDateLong(date) : null;
  }

  get owners() {
    return this.property?.owners || [];
  }

  get ownersDeduped() {
    return [...new Set(this.owners)];
  }

  get ownersFormatted() {
    return this.ownersDeduped.map(owner => formatDisplayName(owner));
  }

  get ownerFormatted() {
    return formatOwnerNames(this.ownersFormatted);
  }

  get addressLines() {
    return this.property?.addressLines || [];
  }

  get addressFormatted() {
    if (!this.property) {
      return '';
    }
    const lines = this.normalizeAddressLines(this.property.addressFormatted);
    return lines[0] || this.property.addressFormatted || '';
  }

  get ownerAddressFormatted() {
    return this.property?.ownerAddress || '';
  }

  get ownerAddressLines() {
    const address = this.ownerAddressFormatted || this.addressFormatted;
    return this.normalizeAddressLines(address);
  }

  get listingUrl() {
    return this._listingUrl;
  }

  get printOptions() {
    return (this.city?.getLetters() || []).map(option => {
      return {
        ...option,
        disabled: Boolean(option.licenseRequired && !this.license)
      }
    });
  }

  get listingId() {
    return this._listingId;
  }

  get evidence() {
    return this._evidence;
  }

  get hasEvidenceScreenshot() {
    const status = this._evidence?.status;
    return status === 'captured' || status === 'manual_upload';
  }

  normalizeAddressLines(address) {
    return normalizeAddressLinesUtil(address);
  }

  normalizeAddressCasing(address) {
    return normalizeAddressCasingUtil(address);
  }

  /**
   * Return a new LetterContext with evidence and listingId overridden.
   */
  withEvidence(evidence, listingId) {
    return new LetterContext({
      property: this._property,
      license: this._license,
      listingUrl: this._listingUrl,
      evidence: evidence || null,
      listingId: listingId || null
    });
  }
}

export function buildLetterContext(options = {}) {
  return new LetterContext(options);
}

/**
 * Builds a LetterContext from a correspondence instance by inspecting its parent model.
 * Returns null when no usable parent model is available.
 */
export function buildLetterContextForCorrespondence(correspondence) {
  const parent = correspondence?.parent || null;
  if (!parent) {
    return null;
  }

  const collectionName = parent?.constructor?.collectionName || null;

  if (collectionName === 'properties') {
    // Property correspondences: evidence is resolved lazily via API, not available here
    return buildLetterContext({ property: parent });
  }

  if (collectionName === 'listings') {
    return buildLetterContext({
      property: parent.verifiedProperty || null,
      listingUrl: parent.url || null,
      evidence: parent.evidence || null,
      listingId: parent.id || null
    });
  }

  return null;
}

/**
 * Fetch evidence for a property from the API.
 * Returns { evidence, listingId } or { evidence: null, listingId: null }.
 */
export async function fetchPropertyEvidence(cityId, propertyId) {
  try {
    const { fetchApi } = await import('../api.js');
    return await fetchApi(`/v1/cities/${cityId}/properties/${propertyId}/evidence`);
  } catch (err) {
    console.error('Failed to fetch property evidence:', err);
    return { evidence: null, listingId: null };
  }
}

/**
 * If a context is missing evidence (e.g. property correspondence), resolve it via the API.
 * Returns a new context with evidence populated, or the original if already present.
 */
export async function resolveContextEvidence(context) {
  if (!context || (context.evidence && context.listingId)) return context;

  const cityId = context.cityId;
  const propertyId = context.property?.id;
  if (!cityId || !propertyId) return context;

  const { evidence, listingId } = await fetchPropertyEvidence(cityId, propertyId);
  if (!evidence && !listingId) return context;

  return context.withEvidence(evidence || context.evidence, listingId || context.listingId);
}
